import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, API_BASE_URL, ApiError } from "../lib/api";
import { safeDocumentSrc } from "../lib/safeDocumentSrc";
import { SIGNED_LINK_REFRESH_MARGIN_MS, signedLinkExpiryMs, signedLinkExpiryPhrase } from "../pwa/signedLinks";

function isImage(url: string) {
  return /\.(png|jpe?g|webp|gif|svg)(\?|#|$)/i.test(url);
}

function absoluteUrl(url: string) {
  if (/^https?:\/\//i.test(url)) return url;
  if (typeof window === "undefined") return url;
  return new URL(url, window.location.origin).toString();
}

function registrationFileUrl(path: string | null | undefined) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const appBase = API_BASE_URL.replace(/\/api\/v2$/, "").replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${appBase}${normalized}`;
}

type CredentialPhotoAdjustment = {
  offset_x: number;
  offset_y: number;
  scale: number;
};

const DEFAULT_PHOTO_ADJUSTMENT: CredentialPhotoAdjustment = { offset_x: 0, offset_y: 0, scale: 1 };

export default function AdminDocumentViewerPage() {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  // Solo archivos de /uploads de torneos123: nunca `javascript:` ni otro sitio.
  const [currentSrc, setCurrentSrc] = useState(() => safeDocumentSrc(searchParams.get("src")));
  const [adjustment, setAdjustment] = useState<CredentialPhotoAdjustment>(DEFAULT_PHOTO_ADJUSTMENT);
  const [savingCredential, setSavingCredential] = useState(false);
  const [credentialMessage, setCredentialMessage] = useState<string | null>(null);
  const label = searchParams.get("label") ?? "Documento";
  const back = searchParams.get("back") || "/admin";
  const safeBack = back.startsWith("/") && !back.startsWith("//") && !back.includes("\\") ? back : "/admin";
  const tournamentId = Number(searchParams.get("tournamentId") ?? "");
  const orderId = Number(searchParams.get("orderId") ?? "");
  const canAdjustCredential = searchParams.get("credential") === "1" && Number.isFinite(tournamentId) && Number.isFinite(orderId) && tournamentId > 0 && orderId > 0;
  const shareUrl = useMemo(() => (currentSrc ? absoluteUrl(currentSrc) : ""), [currentSrc]);
  // CURP, foto y comprobante: la liga es firmada y vence (hora fijada cuando
  // la pantalla de origen cargó sus datos, no cuando se abre el visor).
  const linkExpiry = useMemo(() => signedLinkExpiryMs(currentSrc), [currentSrc]);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  function updateAdjustment(key: keyof CredentialPhotoAdjustment, value: number) {
    setAdjustment((current) => ({ ...current, [key]: value }));
  }

  async function handleRegenerateCredential() {
    if (!token || !canAdjustCredential) return;
    setSavingCredential(true);
    setCredentialMessage(null);
    try {
      const response = await api.registrations.generateCredential(token, tournamentId, orderId, adjustment);
      const nextUrl = response.data.order.participant?.registration_documents?.credential_url;
      const safeNextUrl = nextUrl ? safeDocumentSrc(registrationFileUrl(nextUrl)) : "";
      if (safeNextUrl) {
        setCurrentSrc(safeNextUrl);
        setCredentialMessage("Credencial regenerada con los ajustes de foto.");
      } else {
        setCredentialMessage("Credencial regenerada, pero no recibimos la nueva liga.");
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setCredentialMessage(err.message || "No pudimos regenerar la credencial.");
      } else {
        setCredentialMessage("Error de red. Intenta otra vez.");
      }
    } finally {
      setSavingCredential(false);
    }
  }

  function expiryNote(now: number): string {
    if (linkExpiry === null) return "";
    const note = ` ${signedLinkExpiryPhrase(linkExpiry, now)}`;
    // Poca vida: quien la reciba quizá la abra tarde.
    return linkExpiry - now < SIGNED_LINK_REFRESH_MARGIN_MS
      ? `${note} Para mandar una liga con más tiempo, usa «Volver» y abre el archivo otra vez.`
      : note;
  }

  async function handleShare() {
    if (!shareUrl) return;
    const now = Date.now();
    if (linkExpiry !== null && linkExpiry <= now) {
      setShareMessage("Esta liga ya venció. Usa «Volver», abre el archivo otra vez y compártelo desde ahí.");
      return;
    }
    const copiedMessage = linkExpiry === null ? "Liga copiada para compartir." : `Liga copiada.${expiryNote(now)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: label, url: shareUrl });
        if (linkExpiry !== null) setShareMessage(`Liga compartida.${expiryNote(now)}`);
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage(copiedMessage);
    } catch {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareMessage(copiedMessage);
      } catch {
        setShareMessage("No se pudo compartir automáticamente. Copia la liga desde el navegador.");
      }
    }
  }

  if (!currentSrc) {
    return (
      <section className="document-viewer-page document-viewer-page--direct">
        <div className="document-viewer-toolbar">
          <Link className="button button--ghost" to={safeBack}>← Volver</Link>
        </div>
        <p className="form-error">No se recibió el documento.</p>
      </section>
    );
  }

  return (
    <section className="document-viewer-page document-viewer-page--direct">
      <div className="document-viewer-toolbar" aria-label="Acciones del documento">
        <Link className="button button--ghost" to={safeBack}>← Volver</Link>
        <button className="button button--primary" type="button" onClick={handleShare}>Compartir</button>
      </div>
      {shareMessage ? <p className="document-viewer-share-message" role="status">{shareMessage}</p> : null}
      {canAdjustCredential ? (
        <section className="document-credential-adjuster" aria-label="Ajustar foto de credencial">
          <div className="credential-photo-controls credential-photo-controls--viewer">
            <label>
              Zoom
              <input
                max="2.5"
                min="0.7"
                onChange={(event) => updateAdjustment("scale", Number(event.target.value))}
                step="0.05"
                type="range"
                value={adjustment.scale}
              />
            </label>
            <label>
              ↔ Centrar
              <input
                max="160"
                min="-160"
                onChange={(event) => updateAdjustment("offset_x", Number(event.target.value))}
                step="5"
                type="range"
                value={adjustment.offset_x}
              />
            </label>
            <label>
              ↕ Centrar
              <input
                max="160"
                min="-160"
                onChange={(event) => updateAdjustment("offset_y", Number(event.target.value))}
                step="5"
                type="range"
                value={adjustment.offset_y}
              />
            </label>
            <button className="button button--sm button--ghost" onClick={() => setAdjustment(DEFAULT_PHOTO_ADJUSTMENT)} type="button">
              Reset foto
            </button>
          </div>
          <button className="button button--primary document-credential-adjuster__action" disabled={savingCredential} onClick={() => void handleRegenerateCredential()} type="button">
            {savingCredential ? "Regenerando…" : "Regenerar con ajustes"}
          </button>
          {credentialMessage ? <p className="document-viewer-share-message" role="status">{credentialMessage}</p> : null}
        </section>
      ) : null}
      <div className="document-viewer-frame document-viewer-frame--direct">
        {failedSrc === currentSrc ? (
          // Aviso fijo en lugar de la imagen rota (el lector de pantalla lo
          // anuncia; sin toast: GlobalToasts ignora data-no-toast).
          <p className="form-error" role="status" data-no-toast>
            No se pudo abrir el archivo. Si la liga ya venció, usa «Volver» y ábrelo otra vez.
          </p>
        ) : isImage(currentSrc) ? (
          <img src={currentSrc} alt={label} onError={() => setFailedSrc(currentSrc)} />
        ) : (
          <iframe title={label} src={currentSrc} />
        )}
      </div>
    </section>
  );
}
